import { zodResolver } from '@hookform/resolvers/zod';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle
} from '@/components/ui/dialog';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { useUpdateUserEmailMutation } from '@/services/user/queries';
import { useAuthStore } from '@/store/auth';

const formSchema = z.object({
  newEmail: z.string().email('Please enter a valid email address'),
  confirmEmail: z.string()
}).refine((data) => data.newEmail === data.confirmEmail, {
  message: "Emails don't match",
  path: ['confirmEmail']
});

interface UpdateEmailDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function UpdateEmailDialog({
  open,
  onOpenChange
}: UpdateEmailDialogProps) {
  const userId = useAuthStore((state) => state.user?.id) || '';
  const currentEmail = useAuthStore((state) => state.user?.email) || '';

  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      newEmail: '',
      confirmEmail: ''
    }
  });

  const { mutate: updateEmail, isPending: isLoading } = useUpdateUserEmailMutation(
    userId,
    () => {
      toast.success('Your email has been updated successfully.');
      form.reset();
      onOpenChange(false);
    },
    (error: any) => {
      toast.error(error.message || 'Failed to update email. Please try again.');
    }
  );

  async function onSubmit(values: z.infer<typeof formSchema>) {
    updateEmail({
      newEmail: values.newEmail
    });
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className='sm:max-w-[425px]'>
        <DialogHeader>
          <DialogTitle>Update Email Address</DialogTitle>
          <DialogDescription>
            Your current email is: <strong>{currentEmail}</strong>
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className='space-y-4'>
            <FormField
              control={form.control}
              name='newEmail'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>New Email Address</FormLabel>
                  <FormControl>
                    <Input type='email' placeholder='Enter new email address' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <FormField
              control={form.control}
              name='confirmEmail'
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Confirm New Email</FormLabel>
                  <FormControl>
                    <Input type='email' placeholder='Confirm new email address' {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />
            <DialogFooter>
              <Button
                type='button'
                variant='outline'
                onClick={() => onOpenChange(false)}
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button type='submit' disabled={isLoading} isLoading={isLoading}>
                Update Email
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}